// root/frontend/pages/Footer.jsx
import React from "react";
import { Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useModal } from "../context/modalContext";
import Modal from "../modal/Modal";
import {
  ContactUsContent,
  SettingsContent,
  FeedbackContent,
} from "../modal/ModalContents";
import { TypingHelpContent } from "../modal/TypingHelpContent";
import { PrivacyPolicyModal } from "../modal/PrivacyPolicyModal";
import styles from "./Footer.module.css";

const Footer = () => {
  const { activeModal, openModal } = useModal();

  const token = localStorage.getItem("token");
  const isAuthenticated = !!token;

  // Extract user data from JWT token (same pattern as Header)
  const getUserData = () => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return {
        name: decoded.name || decoded.given_name || "Student",
        email: decoded.email || "No email",
        class_name: decoded.class_name || "Class not assigned",
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const userData = getUserData();

  // Function to open YouTube channel in new window
  const openYouTubeChannel = () => {
    window.open(
      "https://youtube.com/@jigyasa-ai-hindi",
      "_blank",
      "noopener,noreferrer"
    );
  };

  // Modal content renderer
  const renderModalContent = () => {
    switch (activeModal) {
      case "contact":
        return <ContactUsContent />;
      case "typing-help":
        return <TypingHelpContent />;
      case "settings":
        return (
          <SettingsContent
            isAuthenticated={isAuthenticated}
            userData={userData}
          />
        );
      case "feedback":
        return (
          <FeedbackContent
            isAuthenticated={isAuthenticated}
            userData={userData}
          />
        );
      case "privacy":
        return <PrivacyPolicyModal />;
      default:
        return null;
    }
  };

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          {/* Left Section - Logo and Company Info */}
          <div className={styles.footerSection}>
            <div className={styles.logoSection}>
              <img
                src="/logojigyasaai.png"
                alt="Jigyasa AI Logo"
                className={styles.logoImage}
              />
              <div className={styles.logoText}>Jigyasa AI</div>
            </div>
            <p className={styles.companyDescription}>
              Empowering learning through artificial intelligence
            </p>
          </div>

          {/* Right Section - Quick Links and Social */}
          <div className={styles.footerSection}>
            <div className={styles.linksList}>
              <Link to="/about" className={styles.footerLink}>
                About Us
              </Link>
              <Link to="/user-guide" className={styles.footerLink}>
                User Guide
              </Link>
              <button
                className={styles.footerLink}
                onClick={() => openModal("contact")}
              >
                Contact Us
              </button>
              <button
                className={styles.footerLink}
                onClick={() => openModal("feedback")}
              >
                Feedback
              </button>
              {isAuthenticated && (
                <button
                  className={styles.footerLink}
                  onClick={() => openModal("typing-help")}
                >
                  Hindi Typing Help
                </button>
              )}
              {/* COMMENTING THIS SETTINGS BUTTON HERE TILL WE ARE SURE WHAT WE NEED TO PUT UNDER SETTINGS. UNCOMMENT IT WHEN NEEDED
              <button
                className={styles.footerLink}
                onClick={() => openModal("settings")}
              >
                Settings
              </button>*/}
            </div>

            {/* YouTube Icon */}
            <div className={styles.socialSection}>
              <button
                className={styles.youtubeButton}
                onClick={openYouTubeChannel}
                title="Visit our YouTube channel"
                aria-label="Visit our YouTube channel"
              >
                <svg
                  className={styles.youtubeIcon}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Legal and Rights */}
        <div className={styles.footerBottom}>
          <div className={styles.bottomContent}>
            <p>
              <a
                className={styles.footerLegalLink}
                href="/privacy-policy.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              {" | "}
              <Link className={styles.footerLegalLink} to="/terms-of-service">
                Terms of Service
              </Link>
              {/* COMMENTING THIS COOKIE PART HERE TILL WE ARE SURE WHAT WE NEED TO PUT UNDER COOKIE. UNCOMMENT IT WHEN NEEDED
              {" | Cookie Policy"} */}
            </p>
            <div className={styles.bottomLegal}>
              <p className={styles.rightsText}>
                © 2026 Jigyasa AI. All rights reserved.
              </p>
              <p className={styles.registeredText}>
                Registered trademark of Jigyasa AI.
              </p>
            </div>
            <p>Made with ❤️ for learners everywhere</p>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <Modal>{renderModalContent()}</Modal>
    </>
  );
};

export default Footer;
